import type { ZodError } from 'zod';
import type { ParentActions, ParentResponses } from '@widgets-connect/message/message.types';
import { parseParentResponseResult } from './parentResponse.schemas';

type Action = keyof ParentActions;

function formatZodError(err: ZodError): string {
    return err.issues.map((i) => i.path.length ? `${i.path.join('.')}: ${i.message}` : i.message).join('; ');
}

export async function callParent<A extends Action>(
    action: A,
    payload?: ParentActions[A],
    timeout: number = 10_000
): Promise<A extends keyof ParentResponses ? ParentResponses[A] : void> {
    const id = crypto.randomUUID();
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            window.removeEventListener('message', onMessage);
            reject(new Error('Timeout calling parent'));
        }, timeout);

        function onMessage(e: MessageEvent) {
            if (e.data?.id !== id) {
                return;
            }
            clearTimeout(timer);
            window.removeEventListener('message', onMessage);

            if (e.source !== window.parent) {
                reject(new Error('Parent message source mismatch'));
                return;
            }

            const parsed = parseParentResponseResult(
                action as keyof ParentResponses,
                e.data.result,
                e.origin
            );
            if (!parsed.success) {
                reject(new Error(`Invalid parent response: ${formatZodError(parsed.error)}`));
                return;
            }

            resolve(parsed.data as A extends keyof ParentResponses ? ParentResponses[A] : void);
        }
        window.addEventListener('message', onMessage);
        // TODO: this creates a new listener for all action,
        // but we only need it for a few of them
        // console.log('listener created for action:', action);
        window.parent.postMessage({ id, action, payload }, '*');

    });
}